export interface OfflineSyncStats {
  pendingReviews: number;
  pendingResponses: number;
  failedResponses: number;
  pendingOnDemandItems: number;
  syncErrors: number;
  pendingPhotos: number;
  failedPhotos: number;
  recoverableItems: number;
  recoveryAvailable: boolean;
  waitingToSyncCount: number;
  retryNeededCount: number;
  totalPending: number;
  lastSyncAttempt?: Date;
}

interface OfflineSyncStatusReview {
  id: number;
  syncStatus?: string | null;
  conflictStatus?: string | null;
}

interface OfflineSyncStatusResponse {
  id?: number | null;
  syncStatus?: string | null;
  conflictStatus?: string | null;
}

interface OfflineSyncStatusOnDemandItem {
  review_id: number;
  sync_status?: string | null;
}

interface OfflineSyncStatusDatabaseStats {
  photosQueue: number;
}

interface OfflineSyncStatusRecoverableWorkItem {
  kind?: string | null;
  entityId?: number | null;
  syncStatus?: string | null;
}

export interface OfflineSyncStatusDatabase {
  getReviews(filter?: any): Promise<OfflineSyncStatusReview[]>;
  getResponsesBySyncStatus(syncStatuses: string[]): Promise<any[]>;
  getPendingOfflineOnDemandItems(): Promise<OfflineSyncStatusOnDemandItem[]>;
  getPhotosFromQueue?(filter?: any): Promise<any[]>;
  getDatabaseStats(): Promise<OfflineSyncStatusDatabaseStats>;
  getResponsesByReviewId(reviewId: number): Promise<OfflineSyncStatusResponse[]>;
  updateReview(reviewId: number, updates: any): Promise<void>;
  getRecoverableWorkCount?(): Promise<number>;
  getRecoverableWorkItems?(): Promise<OfflineSyncStatusRecoverableWorkItem[]>;
}

const emptySyncStats = (): OfflineSyncStats => ({
  pendingReviews: 0,
  pendingResponses: 0,
  failedResponses: 0,
  pendingOnDemandItems: 0,
  syncErrors: 0,
  pendingPhotos: 0,
  failedPhotos: 0,
  recoverableItems: 0,
  recoveryAvailable: false,
  waitingToSyncCount: 0,
  retryNeededCount: 0,
  totalPending: 0,
});

const recoveryChoiceConflictStatuses = new Set(['local_wins', 'conflicted', 'restored']);
const activeSyncStatuses = new Set(['pending', 'syncing']);

const needsRecoveryChoice = (row: {
  syncStatus?: string | null;
  conflictStatus?: string | null;
}): boolean =>
  row.conflictStatus === 'conflicted' ||
  (recoveryChoiceConflictStatuses.has(row.conflictStatus || '') &&
    !activeSyncStatuses.has(row.syncStatus || ''));

export class OfflineSyncStatusService {
  constructor(
    private database: OfflineSyncStatusDatabase,
    private logger: Pick<Console, 'warn'> = console
  ) {}

  async getSyncStats(): Promise<OfflineSyncStats> {
    try {
      const pendingReviews = await this.database.getReviews({ syncStatus: 'pending' });
      const failedReviews = await this.database.getReviews({ syncStatus: 'failed' });
      const pendingResponseRows = await this.database.getResponsesBySyncStatus(['pending']);
      const failedResponseRows = await this.database.getResponsesBySyncStatus(['failed']);
      const pendingOnDemandItems = await this.database.getPendingOfflineOnDemandItems();
      const queuedPhotos = await this.database.getPhotosFromQueue?.({}).catch(() => []) || [];
      let recoverableItems = 0;
      let recoverableWorkItems: OfflineSyncStatusRecoverableWorkItem[] = [];
      if (this.database.getRecoverableWorkItems) {
        try {
          recoverableWorkItems = await this.database.getRecoverableWorkItems() ?? [];
          recoverableItems = recoverableWorkItems.length;
        } catch {
          recoverableItems = 0;
          recoverableWorkItems = [];
        }
      } else if (this.database.getRecoverableWorkCount) {
        try {
          recoverableItems = await this.database.getRecoverableWorkCount() ?? 0;
        } catch {
          recoverableItems = 0;
        }
      }

      const recoverablePendingResponseIds = new Set(
        recoverableWorkItems
          .filter(item =>
            item.kind === 'response' &&
            item.entityId != null &&
            activeSyncStatuses.has(item.syncStatus || '')
          )
          .map(item => item.entityId)
      );

      const pendingReviewRows = pendingReviews.filter(review => !needsRecoveryChoice(review));
      const failedReviewRows = failedReviews.filter(review => !needsRecoveryChoice(review));
      const pendingRows = pendingResponseRows.filter(response =>
        !needsRecoveryChoice(response) &&
        !recoverablePendingResponseIds.has(response.id ?? null)
      );
      const failedRows = failedResponseRows.filter(response => !needsRecoveryChoice(response));

      const pendingResponses = pendingRows.length;
      const failedResponses = failedRows.length;
      const pendingPhotos = queuedPhotos.filter(photo =>
        photo.status === 'pending' || photo.status === 'uploading'
      ).length;
      const failedPhotos = queuedPhotos.filter(photo => photo.status === 'failed').length;
      const waitingToSyncCount = (
        pendingReviewRows.length +
        pendingResponses +
        pendingPhotos +
        pendingOnDemandItems.length
      );
      const retryNeededCount = failedReviewRows.length + failedResponses + failedPhotos;
      const totalPending = waitingToSyncCount + retryNeededCount;

      return {
        pendingReviews: pendingReviewRows.length,
        pendingResponses,
        failedResponses,
        pendingOnDemandItems: pendingOnDemandItems.length,
        syncErrors: failedReviewRows.length + failedResponses,
        pendingPhotos,
        failedPhotos,
        recoverableItems,
        recoveryAvailable: recoverableItems > 0,
        waitingToSyncCount,
        retryNeededCount,
        totalPending,
      };
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Error getting sync stats:', error);
      return emptySyncStats();
    }
  }

  async recomputeReviewSyncStatuses(): Promise<void> {
    const allReviews = await this.database.getReviews({});

    for (const review of allReviews) {
      await this.recomputeReviewSyncStatus(review.id);
    }
  }

  async recomputeReviewSyncStatus(reviewId: number): Promise<void> {
    try {
      const responses = await this.database.getResponsesByReviewId(reviewId);
      const hasPendingResponses = responses.some(response =>
        response.syncStatus === 'pending' || response.syncStatus === 'failed' || response.syncStatus === 'syncing'
      );

      const pendingOnDemandItems = await this.database.getPendingOfflineOnDemandItems();
      const hasPendingItems = pendingOnDemandItems.some(item =>
        item.review_id === reviewId && item.sync_status !== 'synced'
      );
      const queuedPhotos = await this.database.getPhotosFromQueue?.({}).catch(() => []) || [];
      const hasPendingPhotos = queuedPhotos.some(photo =>
        (photo.status === 'pending' || photo.status === 'uploading' || photo.status === 'failed') &&
        photo.metadata?.reviewId === reviewId
      );

      if (!hasPendingResponses && !hasPendingItems && !hasPendingPhotos) {
        await this.database.updateReview(reviewId, {
          syncStatus: 'synced',
          syncError: undefined,
        });
      }
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Failed to recompute review sync status:', error);
    }
  }
}
